import { endOfDay, isWithinInterval, addHours, subHours } from 'date-fns'
import { Mission, MissionWithSummary } from '../api/missions'
import { WorkPeriod, WorkPeriodWithSummary } from '../api/work-periods'

export const findWorkPeriodById = <T extends MissionWithSummary | Mission>(
  mission: T,
  workPeriodId: string,
): T['workPeriods'][number] | undefined =>
  mission.workPeriods.find(workPeriod => workPeriod._id === workPeriodId) as
    | T['workPeriods'][number]
    | undefined

export const findPreviousWorkPeriod = (
  mission: Mission,
  workPeriodId: string,
): WorkPeriod | undefined => {
  const currentWorkPeriodIndex = mission.workPeriods.findIndex(
    workPeriod => workPeriod._id === workPeriodId,
  )
  if (currentWorkPeriodIndex === 0) return undefined
  return mission.workPeriods[currentWorkPeriodIndex - 1]
}

export const getWorkPeriodsInInterval = (mission: Mission, start: Date, end: Date): WorkPeriod[] =>
  mission.workPeriods.filter(workPeriod =>
    isWithinInterval(new Date(workPeriod.start.date), {
      start,
      end: endOfDay(end),
    }),
  )

export const findNextWorkPeriod = (
  mission: Mission,
  workPeriodId: string,
): WorkPeriod | undefined => {
  const currentWorkPeriodIndex = mission.workPeriods.findIndex(
    workPeriod => workPeriod._id === workPeriodId,
  )
  if (currentWorkPeriodIndex === mission.workPeriods.length - 1) return undefined
  return mission.workPeriods[currentWorkPeriodIndex + 1]
}

/**
 * Find previous work period in a list sorted by start.date.
 * Used when the list comes from useWorkPeriodsQuery (includes virtual work periods).
 */
export const findPreviousWorkPeriodInList = (
  workPeriods: WorkPeriod[],
  workPeriodId: string,
): WorkPeriod | undefined => {
  const idx = workPeriods.findIndex(wp => wp._id === workPeriodId)
  if (idx <= 0) return undefined
  return workPeriods[idx - 1]
}

/**
 * Find next work period in a list sorted by start.date.
 * Used when the list comes from useWorkPeriodsQuery (includes virtual work periods).
 */
export const findNextWorkPeriodInList = (
  workPeriods: WorkPeriod[],
  workPeriodId: string,
): WorkPeriod | undefined => {
  const idx = workPeriods.findIndex(wp => wp._id === workPeriodId)
  if (idx < 0 || idx === workPeriods.length - 1) return undefined
  return workPeriods[idx + 1]
}

export const getLastWorkPeriod = (mission: Mission): WorkPeriod =>
  mission.workPeriods[mission.workPeriods.length - 1] as WorkPeriod

export const hasAllWorkPeriodsClocked = (mission: Mission): boolean =>
  mission.workPeriods.every(
    workPeriod =>
      (workPeriod.start.manualClocking || workPeriod.start.clocking) &&
      (workPeriod.end.manualClocking || workPeriod.end.clocking),
  )

/**
 * Find the most relevant work period:
 * - Currently active
 * - Or starting in the next 24h
 * - Or ended in the last 4h
 */
export const findRelevantWorkPeriod = (mission: Mission): WorkPeriod | undefined => {
  const now = new Date()
  const next24h = addHours(now, 24)
  const last4h = subHours(now, 48)

  return mission.workPeriods
    .filter(wp => {
      const startDate = new Date(wp.start.date)
      const endDate = new Date(wp.end.date)

      return (
        // Work period en cours
        isWithinInterval(now, { start: startDate, end: endDate }) ||
        // Work period dans les prochaines 24h
        isWithinInterval(startDate, { start: now, end: next24h }) ||
        // Work period terminée dans les 4 dernières heures
        isWithinInterval(endDate, { start: last4h, end: now })
      )
    })
    .sort((a, b) => new Date(a.start.date).getTime() - new Date(b.start.date).getTime())[0]
}
